import { toBusyClosesDemoId } from "./busyClosesDemoIds";

const FNV_OFFSET = 0x811c9dc5;
const FNV_PRIME = 0x01000193;
const UINT32_RANGE = 0x1_0000_0000;
const DATASET_SLOT_COUNT = 0x1000_0000;
const ITEMS_PER_SLOT = 0x1_0000;

const KIND_DIGITS = {
    close: 1,
    task: 2,
    run: 3,
    event: 4,
    entity: 5,
    fallback: 6,
} as const;

export type DemoIdKind = keyof typeof KIND_DIGITS;
const slotsByDatasetKey = new Map<string, number>();

/** A 32-bit FNV-1a hash of `text`. */
const hashDemoSeed = (text: string): number => {
    let hash = FNV_OFFSET;
    for (const character of text) {
        hash ^= character.charCodeAt(0);
        hash = Math.imul(hash, FNV_PRIME) >>> 0;
    }
    return hash;
};

export interface DemoRandom {
    /** A float in [0, 1). */
    next: () => number;
    /** An integer in [min, max]. */
    int: (min: number, max: number) => number;
    pick: <T>(items: readonly T[]) => T;
    shuffle: <T>(items: readonly T[]) => T[];
}

/** A seeded mulberry32 generator, so the same seed always builds the same demo. */
export const createDemoRandom = (seed: string): DemoRandom => {
    let state = hashDemoSeed(seed);
    const next = () => {
        state = (state + 0x6d2b79f5) >>> 0;
        let value = state;
        value = Math.imul(value ^ (value >>> 15), value | 1);
        value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
        return ((value ^ (value >>> 14)) >>> 0) / UINT32_RANGE;
    };
    const int = (min: number, max: number) =>
        min + Math.floor(next() * (max - min + 1));
    return {
        next,
        int,
        pick: (items) => items[int(0, items.length - 1)],
        shuffle: (items) => {
            const shuffled = [...items];
            for (let index = shuffled.length - 1; index > 0; index--) {
                const swapIndex = int(0, index);
                [shuffled[index], shuffled[swapIndex]] = [
                    shuffled[swapIndex],
                    shuffled[index],
                ];
            }
            return shuffled;
        },
    };
};

export type DemoIdFactory = (kind: DemoIdKind, index: number) => string;

/** Demo ids for one dataset: a digit per kind, then a slot each dataset key gets on first use this session, then the index (under 0x10000), so ids never collide across kinds or datasets. */
export const createDemoIdFactory = (datasetKey: string): DemoIdFactory => {
    const slot = slotsByDatasetKey.get(datasetKey) ?? slotsByDatasetKey.size;
    slotsByDatasetKey.set(datasetKey, slot);
    return (kind, index) =>
        toBusyClosesDemoId(
            (KIND_DIGITS[kind] * DATASET_SLOT_COUNT + slot) * ITEMS_PER_SLOT +
                index,
        );
};
